/**
 * A function that resolves an icon name to an SVG string.
 *
 * Receives the icon name with the prefix stripped — e.g. for `flag:nl`,
 * the provider registered under `"flag"` receives `"nl"`.
 *
 * Return a valid SVG string if the icon is found, or `null` to indicate
 * the icon is not available.
 */
export type IconProvider = (name: string) => string | null | Promise<string | null>;

const providers = new Map<string, IconProvider>();

/**
 * Registers an icon provider for a given prefix.
 *
 * When `e-icon` receives a name in `prefix:icon` format that is not in the
 * built-in CDN set, the provider registered for that prefix is called with
 * the icon portion only (e.g. `"nl"` for `"flag:nl"`).
 *
 * Registering a second provider for the same prefix replaces the first.
 *
 * @example <caption>Flag icons via flagcdn.com</caption>
 * ```ts
 * registerIconProvider("flag", async (name) => {
 *   const res = await fetch(`https://flagcdn.com/${name}.svg`);
 *   return res.ok ? res.text() : null;
 * });
 * ```
 *
 * @example <caption>Custom internal icon source</caption>
 * ```ts
 * registerIconProvider("internal", async (name) => {
 *   const res = await fetch(`https://my-cdn.example.com/icons/${name}.svg`);
 *   return res.ok ? res.text() : null;
 * });
 * ```
 */
export const registerIconProvider = (prefix: string, provider: IconProvider): void => {
  providers.set(prefix, provider);
};

const splitName = (name: string): { prefix: string; iconName: string } | null => {
  const colonIndex = name.indexOf(":");
  if (colonIndex === -1) return null;
  return { prefix: name.slice(0, colonIndex), iconName: name.slice(colonIndex + 1) };
};

/** @internal */
export const hasProviderForName = (name: string): boolean => {
  const parts = splitName(name);
  return parts !== null && providers.has(parts.prefix);
};

/** @internal */
export const fetchFromProviders = async (name: string): Promise<string | null> => {
  const parts = splitName(name);
  if (!parts) return null;
  const provider = providers.get(parts.prefix);
  if (!provider) return null;
  return (await provider(parts.iconName)) ?? null;
};

/** @internal — for use in tests only */
export const _clearIconProviders = (): void => {
  providers.clear();
};
